'use client';

import type { CSSProperties, TextareaHTMLAttributes } from 'react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { cx } from '../utils/cx';
import { Label } from './label';

export type TextareaSize = 'sm' | 'md' | 'lg';
export type TextareaVariant = 'default' | 'composer';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  helperText?: string;
  hideLabel?: boolean;
  size?: TextareaSize;
  variant?: TextareaVariant;
  autoGrow?: boolean;
  minHeightPx?: number;
  maxHeightPx?: number;
  autoFocusOnMount?: boolean;
  sendOnEnter?: boolean;
  sendButtonSelector?: string;
  clearOnSubmit?: boolean;
  scrollTargetSelectorOnSubmit?: string;
  /**
   * Keeps the field (and the optional scroll target) visible while the mobile
   * on-screen keyboard opens/closes. Uses visualViewport when available.
   */
  keepVisibleOnFocus?: boolean;
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function sizeClass(size: TextareaSize): string {
  switch (size) {
    case 'sm':
      return 'px-3 py-2 text-sm';
    case 'lg':
      return 'px-4 py-3 text-base';
    case 'md':
    default:
      return 'px-3 py-2.5 text-sm';
  }
}

function variantClass(variant: TextareaVariant, autoGrow: boolean): string {
  if (variant === 'composer') {
    // NOTE: 16px (`text-base`) is deliberate. iOS Safari zooms the entire page
    // when a focused control renders below 16px. `touch-manipulation` removes
    // double-tap zoom on the same element.
    return cx(
      'block w-full resize-none rounded-2xl border border-transparent bg-transparent px-3 py-2.5 text-base leading-6 text-secondary shadow-none placeholder:text-tertiary',
      'touch-manipulation',
      'focus-visible:border-transparent focus-visible:bg-transparent focus-visible:shadow-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 outline-none',
      autoGrow && 'overflow-hidden',
    );
  }

  return cx(
    'block w-full rounded-lg border border-secondary_alt/80 bg-primary text-secondary shadow-xs transition-[border-color,background-color,box-shadow] placeholder:text-tertiary',
    'focus-visible:border-brand focus-visible:bg-white focus-visible:shadow-sm focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 outline-none',
    autoGrow ? 'resize-none overflow-hidden' : 'resize-y',
  );
}

function findSendButton(textarea: HTMLTextAreaElement | null, selector: string): HTMLButtonElement | null {
  if (!textarea) return null;

  const form = textarea.form;
  if (form) {
    const candidate = form.querySelector(selector);
    if (candidate instanceof HTMLButtonElement) return candidate;
  }

  let el: HTMLElement | null = textarea;
  while (el) {
    const candidate = el.querySelector(selector);
    if (candidate instanceof HTMLButtonElement) return candidate;
    el = el.parentElement;
  }

  const globalBtn = document.querySelector(selector);
  return globalBtn instanceof HTMLButtonElement ? globalBtn : null;
}

function submitCurrentForm(textarea: HTMLTextAreaElement | null, submitter?: HTMLButtonElement | null): boolean {
  const form = textarea?.form;
  if (!form) return false;

  if (submitter && typeof form.requestSubmit === 'function') {
    try {
      form.requestSubmit(submitter);
      return true;
    } catch {
      // fall through to other submission strategies
    }
  }

  if (typeof form.requestSubmit === 'function') {
    try {
      form.requestSubmit();
      return true;
    } catch {
      // fall through to click/dispatch fallback
    }
  }

  if (submitter) {
    try {
      submitter.click();
      return true;
    } catch {
      // ignore and continue
    }
  }

  try {
    return form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  } catch {
    return false;
  }
}

function scrollElementToBottom(selector: string, behavior: ScrollBehavior = 'auto') {
  const target = document.querySelector(selector);
  if (!(target instanceof HTMLElement)) return;

  target.scrollTo({
    top: target.scrollHeight,
    behavior,
  });
}

export function Textarea({
  label,
  helperText,
  hideLabel = false,
  size = 'md',
  variant = 'default',
  className,
  id,
  name,
  required,
  rows = 4,
  autoGrow = false,
  minHeightPx = 44,
  maxHeightPx = 240,
  autoFocusOnMount = false,
  sendOnEnter = false,
  sendButtonSelector = "[data-chat-send-button='true']",
  clearOnSubmit = false,
  scrollTargetSelectorOnSubmit,
  keepVisibleOnFocus = false,
  enterKeyHint,
  onChange,
  onInput,
  onKeyDown,
  onFocus,
  onBlur,
  value,
  style,
  disabled,
  ...rest
}: TextareaProps) {
  const autoId = useId();
  const controlId = id ?? (name ? name : autoId);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const submitEffectTimeoutRef = useRef<number | null>(null);
  const scrollAnimationFrameRef = useRef<number | null>(null);
  const focusVisibilityTimeoutRef = useRef<number | null>(null);
  const isFocusedRef = useRef(false);

  // The controlled/uncontrolled decision must be stable for the lifetime of
  // the element. Re-deciding per render ("value !== undefined") makes the
  // textarea flip from uncontrolled to controlled whenever a bound value goes
  // from undefined to defined, which React treats as an error. Once the
  // textarea has been controlled, it stays controlled (a later `undefined`
  // value is coerced to an empty string).
  const wasControlledRef = useRef(false);
  if (value !== undefined) wasControlledRef.current = true;
  const isControlled = wasControlledRef.current;

  const cancelPendingScroll = useCallback(() => {
    if (scrollAnimationFrameRef.current !== null) {
      window.cancelAnimationFrame(scrollAnimationFrameRef.current);
      scrollAnimationFrameRef.current = null;
    }
  }, []);

  const scheduleScrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      if (!scrollTargetSelectorOnSubmit) return;

      cancelPendingScroll();
      scrollAnimationFrameRef.current = window.requestAnimationFrame(() => {
        scrollAnimationFrameRef.current = null;
        scrollElementToBottom(scrollTargetSelectorOnSubmit, behavior);
      });
    },
    [cancelPendingScroll, scrollTargetSelectorOnSubmit],
  );

  const resizeToContent = useCallback(() => {
    if (!autoGrow) return;

    const el = textareaRef.current;
    if (!el) return;

    el.style.height = 'auto';

    const nextHeight = clamp(el.scrollHeight, minHeightPx, maxHeightPx);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > maxHeightPx ? 'auto' : 'hidden';
  }, [autoGrow, maxHeightPx, minHeightPx]);

  const ensureVisible = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    if (scrollTargetSelectorOnSubmit) {
      scheduleScrollToBottom('auto');
    }

    try {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    } catch {
      // Older browsers without options support: safe to ignore.
    }
  }, [scheduleScrollToBottom, scrollTargetSelectorOnSubmit]);

  const runSubmitSideEffects = useCallback(() => {
    if (submitEffectTimeoutRef.current !== null) {
      window.clearTimeout(submitEffectTimeoutRef.current);
    }

    submitEffectTimeoutRef.current = window.setTimeout(() => {
      if (clearOnSubmit && !isControlled) {
        const el = textareaRef.current;
        if (el) {
          el.value = '';
          resizeToContent();
        }
      }

      if (scrollTargetSelectorOnSubmit) {
        scheduleScrollToBottom('auto');
      }

      submitEffectTimeoutRef.current = null;
    }, 0);
  }, [clearOnSubmit, isControlled, resizeToContent, scheduleScrollToBottom, scrollTargetSelectorOnSubmit]);

  useEffect(() => {
    const form = textareaRef.current?.form;
    if (!form) return;
    if (!clearOnSubmit && !scrollTargetSelectorOnSubmit) return;

    const handleSubmit = () => {
      runSubmitSideEffects();
    };

    form.addEventListener('submit', handleSubmit);

    return () => {
      form.removeEventListener('submit', handleSubmit);
      if (submitEffectTimeoutRef.current !== null) {
        window.clearTimeout(submitEffectTimeoutRef.current);
        submitEffectTimeoutRef.current = null;
      }
    };
  }, [clearOnSubmit, runSubmitSideEffects, scrollTargetSelectorOnSubmit]);

  useEffect(() => {
    if (!scrollTargetSelectorOnSubmit) return;

    const target = document.querySelector(scrollTargetSelectorOnSubmit);
    if (!(target instanceof HTMLElement)) return;

    scheduleScrollToBottom('auto');

    const mutationObserver = new MutationObserver(() => {
      scheduleScrollToBottom('auto');
    });

    mutationObserver.observe(target, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            scheduleScrollToBottom('auto');
          })
        : null;

    resizeObserver?.observe(target);

    return () => {
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
      cancelPendingScroll();
    };
  }, [cancelPendingScroll, scheduleScrollToBottom, scrollTargetSelectorOnSubmit]);

  // Keep the composer above the on-screen keyboard while it animates open.
  useEffect(() => {
    if (!keepVisibleOnFocus) return;
    if (typeof window === 'undefined') return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleViewportChange = () => {
      if (!isFocusedRef.current) return;
      ensureVisible();
    };

    viewport.addEventListener('resize', handleViewportChange);
    viewport.addEventListener('scroll', handleViewportChange);

    return () => {
      viewport.removeEventListener('resize', handleViewportChange);
      viewport.removeEventListener('scroll', handleViewportChange);
    };
  }, [ensureVisible, keepVisibleOnFocus]);

  useEffect(
    () => () => {
      if (focusVisibilityTimeoutRef.current !== null) {
        window.clearTimeout(focusVisibilityTimeoutRef.current);
        focusVisibilityTimeoutRef.current = null;
      }
    },
    [],
  );

  useLayoutEffect(() => {
    if (!autoGrow) return;
    resizeToContent();
  }, [autoGrow, resizeToContent, value]);

  useEffect(() => {
    if (!autoFocusOnMount) return;

    const el = textareaRef.current;
    if (!el || disabled) return;

    const timeout = window.setTimeout(() => {
      el.focus({ preventScroll: true });
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [autoFocusOnMount, disabled]);

  const mergedStyle: CSSProperties | undefined = autoGrow
    ? {
        minHeight: `${minHeightPx}px`,
        maxHeight: `${maxHeightPx}px`,
        ...style,
      }
    : style;

  return (
    <div className="w-full">
      {label && !hideLabel ? (
        <Label htmlFor={controlId} required={required ?? false} helperText={helperText}>
          {label}
        </Label>
      ) : null}

      {label && hideLabel ? (
        <label htmlFor={controlId} className="sr-only">
          {label}
        </label>
      ) : null}

      <div className="relative">
        <textarea
          {...rest}
          id={controlId}
          name={name}
          required={required}
          rows={rows}
          ref={textareaRef}
          {...(isControlled ? { value: value ?? '' } : {})}
          disabled={disabled}
          style={mergedStyle}
          enterKeyHint={enterKeyHint ?? (sendOnEnter ? 'send' : undefined)}
          onChange={(e) => onChange?.(e)}
          onInput={(e) => {
            onInput?.(e);
            resizeToContent();
          }}
          onFocus={(e) => {
            isFocusedRef.current = true;
            onFocus?.(e);

            if (!keepVisibleOnFocus) return;

            if (focusVisibilityTimeoutRef.current !== null) {
              window.clearTimeout(focusVisibilityTimeoutRef.current);
            }

            // Wait a tick for the keyboard/viewport to settle before adjusting.
            focusVisibilityTimeoutRef.current = window.setTimeout(() => {
              focusVisibilityTimeoutRef.current = null;
              if (!isFocusedRef.current) return;
              ensureVisible();
            }, 120);
          }}
          onBlur={(e) => {
            isFocusedRef.current = false;

            if (focusVisibilityTimeoutRef.current !== null) {
              window.clearTimeout(focusVisibilityTimeoutRef.current);
              focusVisibilityTimeoutRef.current = null;
            }

            onBlur?.(e);
          }}
          onKeyDown={(e) => {
            onKeyDown?.(e);
            if (e.defaultPrevented) return;

            if (!sendOnEnter || e.key !== 'Enter') return;
            if ((e as unknown as { isComposing?: boolean }).isComposing) return;
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

            const btn = findSendButton(textareaRef.current, sendButtonSelector);

            e.preventDefault();
            e.stopPropagation();

            if (btn?.disabled) return;

            const submitted = submitCurrentForm(textareaRef.current, btn);
            if (submitted) {
              runSubmitSideEffects();
            }
          }}
          className={cx(variantClass(variant, autoGrow), variant === 'default' ? sizeClass(size) : '', className)}
        />
      </div>
    </div>
  );
}

Textarea.displayName = 'Textarea';